/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import createSagaMiddleware from 'redux-saga';
import { createStore, applyMiddleware, Middleware, Store, combineReducers } from 'redux';
import {
  createErrorLoggerMidleware,
  createErrorRegisterMiddleware,
  ExtendedStore,
  ReducerInjector,
  reducers as libReducers,
  SagaInjector,
  StoreState as LibStoreState,
} from '@wit/vtv-x-foundation-lib';
import tempCoreReducers from '../core/store/ducks';
import appReducers from './ducks';
import createLoggerMidleware from './middleware/loggerMiddleware';
import rootSaga from './saga';
import IFDebug, { DEBUG_ON } from '../core/configs/IFDebug';

const rootReducer = { ...libReducers, ...tempCoreReducers, ...appReducers };

const combinedRootReducer = combineReducers(rootReducer);

export type StoreState = ReturnType<typeof combinedRootReducer> & LibStoreState;

let store: Store;

export default (): ExtendedStore => {
  if (!store) {
    const sagaMiddleware = createSagaMiddleware({
      onError: (error: Error, { sagaStack }: { sagaStack: string }) => {
        if (DEBUG_ON) {
          console.error(error.stack, `\n\n${sagaStack}`);
        }
      },
    });
    const middlewares: Middleware[] = [sagaMiddleware];

    if (DEBUG_ON) {
      const errorLoggerMiddleware = createErrorLoggerMidleware();
      middlewares.push(errorLoggerMiddleware);
    }

    const errorRegisterMiddleware = createErrorRegisterMiddleware();
    middlewares.push(errorRegisterMiddleware);

    if (IFDebug.LOG_REDUX_STATE) {
      const loggerMiddleware = createLoggerMidleware();
      middlewares.push(loggerMiddleware);
    }

    // Create a store with the root reducer function being the one exposed by the manager.
    store = createStore(combinedRootReducer, applyMiddleware(...middlewares));

    // Put the reducer and saga injectors on the store so it is easily accessible
    (store as ExtendedStore).injectReducer = ReducerInjector.createReducerInjector(store, rootReducer);
    (store as ExtendedStore).injectSaga = SagaInjector.createSagaInjector(sagaMiddleware.run, rootSaga);
  }
  return store as ExtendedStore;
};
